import React, { useState } from 'react';
import api, { fileUrl, openAuthenticatedFile } from '@/api';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';
import { ChartLine, CaretDown, CaretUp, ArrowUp, ArrowDown, Minus, Trophy, FilePdf, ArrowSquareOut, ChatCircleDots, Trash, MagnifyingGlass, Funnel, X, ChartPie as PieChartIcon, Pencil, DownloadSimple } from '@phosphor-icons/react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { SECTORS } from '@/utils/sector_options';
import { formatPhone } from '@/utils/phone';

const MomBadge = ({ value, suffix = '%', small = false }) => {
  if (value === undefined || value === null) return <span className={`${small ? 'text-[10px]' : 'text-xs'} text-text-muted`}>--</span>;
  const isPos = value > 0;
  const color = value === 0 ? 'text-text-muted' : isPos ? 'text-success' : 'text-danger';
  const Icon = isPos ? ArrowUp : value < 0 ? ArrowDown : Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 ${small ? 'text-[10px]' : 'text-xs'} ${color}`}>
      <Icon size={small ? 10 : 12} weight="bold" />
      {value > 0 ? '+' : ''}{value}{suffix}
    </span>
  );
};

const tooltipStyle = { backgroundColor: '#111118', border: '1px solid #2a2a3a', borderRadius: '6px', fontSize: '11px', color: '#f0f0f5', padding: '6px 10px' };
const PIE_COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#3b82f6','#14b8a6'];

const Sparkline = ({ data, dataKey, color, height = 28 }) => {
  if (!data || data.length < 2) return <div style={{ height }} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data}>
        <defs><linearGradient id={`sp-${dataKey}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={color} stopOpacity={0.15}/><stop offset="95%" stopColor={color} stopOpacity={0}/></linearGradient></defs>
        <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5} fill={`url(#sp-${dataKey})`} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const ChartCard = ({ title, data, dataKey, color, formatter, type = 'area' }) => {
  const hasData = data && data.length > 1;
  return (
    <div className="bg-background rounded-lg p-4 border border-border">
      <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">{title}</div>
      {hasData ? (
        <ResponsiveContainer width="100%" height={200}>
        {type === 'bar' ? (
          <BarChart data={data}>
            <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatter ? formatter(v) : v, title]} />
            <Bar dataKey={dataKey} fill={color} radius={[3, 3, 0, 0]} maxBarSize={20} />
          </BarChart>
        ) : type === 'line' ? (
          <LineChart data={data}>
            <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatter ? formatter(v) : v, title]} />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
          </LineChart>
        ) : (
          <AreaChart data={data}>
            <defs><linearGradient id={`mc-${dataKey}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={color} stopOpacity={0.2}/><stop offset="95%" stopColor={color} stopOpacity={0}/></linearGradient></defs>
            <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatter ? formatter(v) : v, title]} />
            <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#mc-${dataKey})`} dot={false} activeDot={{ r: 3 }} />
          </AreaChart>
        )}
      </ResponsiveContainer>
      ) : (
        <div className="h-[200px] flex items-center justify-center text-xs text-text-muted">No data yet</div>
      )}
    </div>
  );
};

export const ExpandedDetail = ({ company, onDelete, onRoundEdit, incubator = false }) => {
  const { formatCompact } = useCurrency();
  const navigate = useNavigate();
  const c = company;
  const q = c.qualitative || {};
  const eq = c.equity;

  const handleAskAI = () => {
    if (incubator) {
      window.dispatchEvent(new CustomEvent('fb-incubator-ai', { detail: { prompt: `Analyse ${c.startup_name}: financial health, growth trajectory and key risks.` } }));
      return;
    }
    // Store context for AI chat
    sessionStorage.setItem('ai_context', `Tell me everything about ${c.startup_name}. Analyse their financial health, growth trajectory, and key risks.`);
    navigate('/vc/chat');
  };

  return (
    <div className="px-6 pb-6 pt-4" data-testid={`expanded-${c.id}`}>
      {/* Two-column layout: Charts left, Info right */}
      <div className="flex gap-5">
        {/* LEFT: Charts (always 6 slots) */}
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-2 gap-3">
            <ChartCard title="MRR" data={c.chart_data} dataKey="mrr" color="#10b981" formatter={(v) => formatCompact(v)} type="area" />
            <ChartCard title="Revenue" data={c.chart_data} dataKey="revenue" color="#3b82f6" formatter={(v) => formatCompact(v)} type="bar" />
            <ChartCard title="Cash in Bank" data={c.chart_data} dataKey="cash" color="#6366f1" formatter={(v) => formatCompact(v)} type="area" />
            <ChartCard title="Runway (Months)" data={c.chart_data?.filter(d => d.runway !== null)} dataKey="runway" color="#f59e0b" formatter={(v) => `${v} mo`} type="line" />
            <ChartCard title="Total Customers" data={c.chart_data} dataKey="customers" color="#8b5cf6" formatter={(v) => v.toLocaleString()} type="area" />
            <ChartCard title="Headcount" data={c.chart_data} dataKey="headcount" color="#ec4899" formatter={(v) => v.toString()} type="bar" />
          </div>
        </div>

        {/* RIGHT: My Investment, Benchmarks, Equity, Qualitative, Actions */}
        <div className="w-[340px] shrink-0 space-y-3">
          {/* My Investment Summary Card */}
          {c.my_investment ? (
            <div className="bg-background rounded-lg p-4 border border-primary/20" data-testid={`my-investment-${c.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-3">My Investment</div>
              {c.my_investment.fund_names.length > 0 && (
                <div className="mb-3 pb-3 border-b border-border">
                  <div className="text-[10px] text-text-muted uppercase tracking-wider mb-1">From Fund{c.my_investment.fund_names.length > 1 ? 's' : ''}</div>
                  <div className="text-[13px] font-medium text-text-primary">{c.my_investment.fund_names.join(' · ')}</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <div>
                  <div className="text-text-muted text-[10px] uppercase tracking-wider">Total Invested</div>
                  <div className="font-mono font-semibold text-text-primary">{formatCompact(c.my_investment.total_invested)}</div>
                </div>
                <div>
                  <div className="text-text-muted text-[10px] uppercase tracking-wider">MoIC</div>
                  <div className={`font-mono font-semibold ${c.moic?.moic ? (c.moic.moic >= 3 ? 'text-success' : c.moic.moic >= 1 ? 'text-info' : 'text-danger') : 'text-text-muted'}`}>
                    {c.moic?.moic ? `${c.moic.moic}x` : 'Pending'}
                  </div>
                </div>
                {c.my_investment.latest_round && (
                  <>
                    <div>
                      <div className="text-text-muted text-[10px] uppercase tracking-wider">Latest Round</div>
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-text-primary">{c.my_investment.latest_round.round || '--'}{c.my_investment.latest_round.investment_year ? ` · ${c.my_investment.latest_round.investment_year}` : ''}</div>
                        {c.my_investment.latest_round.id && onRoundEdit && (
                          <button
                            data-testid={`edit-round-${c.my_investment.latest_round.id}`}
                            onClick={(e) => { e.stopPropagation(); onRoundEdit(c.my_investment.latest_round); }}
                            className="text-text-muted hover:text-primary transition-colors"
                            title="Edit round details"
                          >
                            <Pencil size={11} weight="bold" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-text-muted text-[10px] uppercase tracking-wider">Round Valuation</div>
                      <div className="font-mono text-text-primary">{c.my_investment.latest_round.post_money_valuation ? formatCompact(c.my_investment.latest_round.post_money_valuation) : '--'}</div>
                    </div>
                  </>
                )}
                <div className="col-span-2 pt-2 border-t border-border">
                  <div className="text-text-muted text-[10px] uppercase tracking-wider">Current Valuation (self-reported)</div>
                  <div className="font-mono font-semibold text-text-primary">{c.my_investment.current_valuation ? formatCompact(c.my_investment.current_valuation) : <span className="text-text-muted italic">Not reported by founder</span>}</div>
                </div>
              </div>
              {c.my_investment.rounds.length > 1 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">All Rounds ({c.my_investment.rounds.length})</div>
                  <div className="space-y-1">
                    {c.my_investment.rounds.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-[11px]">
                        <span className="text-text-secondary">{r.round || 'Round'}{r.investment_year ? ` · ${r.investment_year}` : ''}</span>
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-text-primary">{formatCompact(r.investment_amount)}</span>
                          {r.id && onRoundEdit && (
                            <button
                              data-testid={`edit-round-${r.id}`}
                              onClick={(e) => { e.stopPropagation(); onRoundEdit(r); }}
                              className="text-text-muted hover:text-primary transition-colors"
                              title="Edit round details"
                            >
                              <Pencil size={10} weight="bold" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-background rounded-lg p-4 border border-border" data-testid={`my-investment-${c.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">My Investment</div>
              <div className="text-xs text-text-muted">No verified investment rounds yet — invite this startup with investment details for MoIC tracking.</div>
            </div>
          )}

          {/* Benchmarks */}
          <div className="bg-background rounded-lg p-4 border border-border">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-3">Benchmark Rankings</div>
            {c.benchmark_highlights && c.benchmark_highlights.length > 0 ? (
              <div className="space-y-2">
                {c.benchmark_highlights.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-[12px]">
                    <Trophy size={12} weight="fill" className="text-primary flex-shrink-0" />
                    <span className="text-primary font-semibold">Top {h.top_pct}%</span>
                    <span className="text-text-secondary">{h.metric}</span>
                    <span className="text-[10px] text-text-muted ml-auto">({h.filter_label})</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-text-muted">No benchmark data yet</div>
            )}
          </div>

          {/* Equity Cap Table */}
          <div className="bg-background rounded-lg p-4 border border-border">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-3">Equity Position</div>
            {eq && eq.slices.length > 0 ? (
              <div>
                <div className="flex justify-center mb-3">
                  <ResponsiveContainer width={140} height={140}>
                    <PieChart>
                      <Pie
                        data={[...eq.slices.map(s => ({ name: s.name, value: s.pct })), { name: 'Founders', value: eq.founder_pct }]}
                        cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" stroke="none"
                      >
                        {eq.slices.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        <Cell fill="#374151" />
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v.toFixed(2)}%`, '']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5">
                  {eq.slices.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-[12px]">
                      <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-text-primary truncate flex-1">{s.name}</span>
                      <span className="font-mono text-text-primary font-semibold">{s.pct}%</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 text-[12px] border-t border-border pt-1.5">
                    <div className="w-2.5 h-2.5 rounded-sm bg-[#374151] flex-shrink-0" />
                    <span className="text-text-secondary flex-1">Founders & Others</span>
                    <span className="font-mono text-text-primary font-semibold">{eq.founder_pct}%</span>
                  </div>
                </div>
                {eq.current_valuation && (
                  <div className="text-[11px] text-text-muted mt-2 pt-2 border-t border-border">
                    Valuation: <span className="font-mono text-text-primary">{formatCompact(eq.current_valuation)}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-text-muted">No equity data — pending verification</div>
            )}
          </div>

          {/* Qualitative */}
          {(q.win || q.challenge || q.goals || q.help_needed || q.trl) && (
            <div className="bg-background rounded-lg p-4 border border-border space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Founder Update</div>
              {q.trl && <div><span className="text-[11px] text-text-secondary font-medium">TRL: </span><span className="text-[12px] text-text-primary">{q.trl}</span></div>}
              {q.win && <div><span className="text-[11px] text-success font-medium">Win: </span><span className="text-[12px] text-text-primary">{q.win}</span></div>}
              {q.challenge && <div><span className="text-[11px] text-danger font-medium">Challenge: </span><span className="text-[12px] text-text-primary">{q.challenge}</span></div>}
              {q.goals && <div><span className="text-[11px] text-info font-medium">Goals: </span><span className="text-[12px] text-text-primary">{q.goals}</span></div>}
              {q.help_needed && <div><span className="text-[11px] text-warning font-medium">Help: </span><span className="text-[12px] text-text-primary">{q.help_needed}</span></div>}
            </div>
          )}

          {/* Details + Actions */}
          <div className="bg-background rounded-lg p-4 border border-border space-y-3">
            <div className="grid grid-cols-2 gap-2 text-[12px]">
              <div><span className="text-text-muted">Founder</span><div className="text-text-primary font-medium truncate">{c.founder_name || '--'}</div></div>
              <div><span className="text-text-muted">Sector</span><div className="text-text-primary font-medium truncate">{c.sector || '--'}</div></div>
              <div><span className="text-text-muted">Stage</span><div className="text-text-primary font-medium truncate">{c.funding_stage || '--'}</div></div>
              <div><span className="text-text-muted">Location</span><div className="text-text-primary font-medium truncate">{c.hq_city || '--'}</div></div>
              <div className="col-span-2">
                <span className="text-text-muted">Email</span>
                {c.founder_email ? (
                  <a href={`mailto:${c.founder_email}`} className="block text-primary hover:text-primary/80 font-medium truncate" data-testid={`founder-email-${c.id}`} title={c.founder_email}>{c.founder_email}</a>
                ) : (
                  <div className="text-text-muted">--</div>
                )}
              </div>
              <div className="col-span-2">
                <span className="text-text-muted">Phone</span>
                {c.founder_phone ? (
                  <a href={`tel:${c.founder_phone}`} className="block text-primary hover:text-primary/80 font-mono font-medium" data-testid={`founder-phone-${c.id}`}>{formatPhone(c.founder_phone)}</a>
                ) : (
                  <div className="text-text-muted italic">Not shared by founder</div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
              {c.has_pitch_deck && c.pitch_deck_file_id && (
                <button onClick={() => openAuthenticatedFile(c.pitch_deck_file_id).catch((err) => {
                  console.error('Failed to open pitch deck:', err);
                  toast.error(err.response?.data?.detail || 'Unable to open pitch deck');
                })} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/8 rounded-lg hover:bg-primary/15 transition-colors" data-testid={`deck-${c.id}`}>
                  <FilePdf size={13} weight="fill" /> Deck
                </button>
              )}
              <button onClick={handleAskAI} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-info bg-info/8 rounded-lg hover:bg-info/15 transition-colors" data-testid={`chat-${c.id}`}>
                <ChatCircleDots size={13} weight="fill" /> Ask AI
              </button>
              {c.website && (
                <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary border border-border rounded-lg hover:bg-surface transition-colors">
                  <ArrowSquareOut size={13} weight="bold" /> Web
                </a>
              )}
              <button onClick={() => onDelete(c.id, c.startup_name)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-danger bg-danger/8 rounded-lg hover:bg-danger/15 transition-colors ml-auto" data-testid={`remove-${c.id}`}>
                <Trash size={13} weight="bold" /> Remove
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
export const DeleteModal = ({ startupName, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');
  const reasons = ['Startup shut down', 'Investment exit (sold/IPO)', 'No longer relevant to thesis', 'Data quality issues', 'Other'];
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="delete-modal">
      <div className="bg-surface border border-border rounded-xl p-6 w-[420px] max-w-[90vw]">
        <h3 className="font-heading font-bold text-lg text-text-primary mb-2">Remove {startupName}?</h3>
        <p className="text-sm text-text-secondary mb-4">This will disconnect them from your portfolio. Their data won't be deleted.</p>
        <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Why are you removing this company?</div>
        <div className="space-y-1.5 mb-4">
          {reasons.map(r => (
            <button key={r} onClick={() => setReason(r)} className={`w-full text-left px-3 py-2 text-sm rounded-lg border transition-colors ${reason === r ? 'border-primary bg-primary/10 text-primary' : 'border-border text-text-secondary hover:bg-background'}`}>
              {r}
            </button>
          ))}
        </div>
        {reason === 'Other' && (
          <input type="text" placeholder="Specify reason..." className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text-primary mb-4 focus:outline-none focus:border-primary" onChange={e => setReason(`Other: ${e.target.value}`)} />
        )}
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2.5 text-sm text-text-secondary border border-border rounded-lg hover:bg-background transition-colors">Cancel</button>
          <button onClick={() => onConfirm(reason)} disabled={!reason} className="flex-1 px-4 py-2.5 text-sm text-white bg-danger rounded-lg hover:bg-danger/90 transition-colors disabled:opacity-50" data-testid="confirm-delete-btn">Remove</button>
        </div>
      </div>
    </div>
  );
};

const ROUND_TYPES = ['Pre-Seed', 'Seed', 'Pre-Series A', 'Series A', 'Series B', 'Series C', 'Series D', 'Series E+', 'Bridge', 'SAFE', 'Convertible Note'];

export const EditRoundModal = ({ round, onClose, onSaved }) => {
  const [form, setForm] = useState({
    round: round.round || '',
    investment_amount: round.investment_amount || '',
    post_money_valuation: round.post_money_valuation || '',
    pre_money_valuation: round.pre_money_valuation || '',
    round_size: round.round_size || '',
    investment_year: round.investment_year || new Date().getFullYear(),
    lead_investor: !!round.lead_investor,
    co_investors: round.co_investors || '',
  });
  const [saving, setSaving] = useState(false);

  const equity = (form.investment_amount > 0 && form.post_money_valuation > 0)
    ? ((Number(form.investment_amount) / Number(form.post_money_valuation)) * 100).toFixed(2)
    : null;

  const handleSave = async () => {
    if (!form.round) { toast.error('Round type is required'); return; }
    if (!form.investment_amount || Number(form.investment_amount) <= 0) { toast.error('Investment amount must be > 0'); return; }
    if (!form.post_money_valuation || Number(form.post_money_valuation) <= 0) { toast.error('Post-money valuation must be > 0'); return; }
    setSaving(true);
    try {
      await api.patch(`/vc/funding-rounds/${round.id}`, {
        round: form.round,
        investment_amount: Number(form.investment_amount),
        post_money_valuation: Number(form.post_money_valuation),
        pre_money_valuation: form.pre_money_valuation ? Number(form.pre_money_valuation) : null,
        round_size: form.round_size ? Number(form.round_size) : null,
        investment_year: form.investment_year ? Number(form.investment_year) : null,
        lead_investor: form.lead_investor,
        co_investors: form.co_investors,
      });
      toast.success('Round updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update round');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-round-modal">
      <div className="bg-surface border border-border rounded-xl p-6 w-[520px] max-w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-lg text-text-primary">Edit Round</h3>
            <p className="text-xs text-text-muted mt-0.5">Update investment, valuation, and round details. MoIC recalculates automatically.</p>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="edit-round-close-btn"><X size={18} weight="bold" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">Round type *</label>
            <select
              data-testid="edit-round-type"
              value={form.round}
              onChange={(e) => setForm({ ...form, round: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-primary"
            >
              <option value="">Select round</option>
              {ROUND_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">Investment amount (Rs.) *</label>
            <input type="number" data-testid="edit-round-amount" value={form.investment_amount}
              onChange={(e) => setForm({ ...form, investment_amount: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-primary" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">Year</label>
            <input type="number" data-testid="edit-round-year" value={form.investment_year}
              onChange={(e) => setForm({ ...form, investment_year: e.target.value })}
              min="1990" max="2099"
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-primary" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">Pre-money (Rs.)</label>
            <input type="number" data-testid="edit-round-pre" value={form.pre_money_valuation}
              onChange={(e) => setForm({ ...form, pre_money_valuation: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-primary" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">Post-money (Rs.) *</label>
            <input type="number" data-testid="edit-round-post" value={form.post_money_valuation}
              onChange={(e) => setForm({ ...form, post_money_valuation: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-primary" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">Round size (Rs.)</label>
            <input type="number" data-testid="edit-round-size" value={form.round_size}
              onChange={(e) => setForm({ ...form, round_size: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-primary" />
          </div>
          <div className="col-span-1">
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" data-testid="edit-round-lead" checked={form.lead_investor}
                onChange={(e) => setForm({ ...form, lead_investor: e.target.checked })}
                className="accent-primary" />
              <span className="text-sm text-text-primary">Lead investor</span>
            </label>
          </div>
          <div className="col-span-2">
            <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1">Co-investors</label>
            <input type="text" data-testid="edit-round-coinv" value={form.co_investors}
              placeholder="Comma-separated names" onChange={(e) => setForm({ ...form, co_investors: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-primary" />
          </div>
        </div>

        {equity && (
          <div className="mt-4 px-3 py-2 bg-primary/5 border border-primary/20 rounded-lg text-xs text-text-secondary">
            <span className="text-text-muted">Implied equity %:</span>{' '}
            <span className="font-mono font-semibold text-primary" data-testid="edit-round-equity">{equity}%</span>
          </div>
        )}

        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm text-text-secondary border border-border rounded-lg hover:bg-background transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving} data-testid="edit-round-save-btn"
            className="flex-1 px-4 py-2.5 text-sm text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50">
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const STAGES = ['All', 'Pre-Seed', 'Seed', 'Pre-Series A', 'Series A', 'Series B', 'Series C', 'Series D+'];

const VCPortfolio = ({ user, portfolio, onRefresh }) => {
  const { formatCompact } = useCurrency();
  const [expandedId, setExpandedId] = useState(null);
  const [sortKey, setSortKey] = useState('startup_name');
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [filterSector, setFilterSector] = useState('All');
  const [filterStage, setFilterStage] = useState('All');
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, name}
  const [editingRound, setEditingRound] = useState(null); // funding round being edited
  const [downloading, setDownloading] = useState(false);

  const agg = portfolio?.aggregates || {};
  const companies = portfolio?.companies || [];

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'startup_name' ? 'asc' : 'desc'); }
  };

  const getSortValue = (c, key) => {
    switch (key) {
      case 'startup_name': return c.startup_name?.toLowerCase() || '';
      case 'mrr': return c.latest_update?.mrr || 0;
      case 'growth': return c.mom?.mrr_pct ?? -999;
      case 'runway': return c.latest_update?.runway_months || 0;
      case 'cash': return c.latest_update?.cash_in_bank || 0;
      case 'churn': return c.latest_update?.churn_pct ?? 999;
      case 'margin': return c.latest_update?.gross_margin_pct || 0;
      case 'moic': return c.moic?.moic ?? -1;
      default: return 0;
    }
  };

  const filtered = companies.filter(c => {
    if (search && !c.startup_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterSector !== 'All' && c.sector !== filterSector) return false;
    if (filterStage !== 'All' && c.funding_stage !== filterStage) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const va = getSortValue(a, sortKey);
    const vb = getSortValue(b, sortKey);
    const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const getHealthDot = (c) => {
    const rm = c.latest_update?.runway_months;
    const growth = c.mom?.mrr_pct;
    if (rm && rm < 3) return 'bg-danger';
    if (rm && rm < 6) return 'bg-warning';
    if (growth !== undefined && growth < -10) return 'bg-danger';
    if (!c.latest_update) return 'bg-text-muted';
    return 'bg-success';
  };

  const downloadPortfolioUpdates = async () => {
    if (downloading || !filtered.length) return;
    try {
      setDownloading(true);
      const response = await api.post('/vc/portfolio/export', { startup_ids: filtered.map(company => company.id) }, { responseType: 'blob' });
      const disposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || 'Portfolio-Monthly-Updates.zip';
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Portfolio monthly updates downloaded');
    } catch (err) {
      console.error('Failed to export portfolio', err);
      toast.error(err.response?.data?.detail || 'Failed to download portfolio data');
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async (reason) => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/vc/portfolio/${deleteTarget.id}`, { data: { reason } });
      toast.success(`${deleteTarget.name} removed from portfolio`);
      setDeleteTarget(null);
      setExpandedId(null);
      if (onRefresh) onRefresh();
    } catch (err) { toast.error('Failed to remove'); }
  };

  const SortHeader = ({ label, sortKeyName, className = '' }) => (
    <button onClick={() => handleSort(sortKeyName)} className={`flex items-center gap-1 text-[11px] uppercase tracking-wider text-text-muted hover:text-text-primary transition-colors ${className}`} data-testid={`sort-${sortKeyName}`}>
      {label}
      {sortKey === sortKeyName && (sortDir === 'asc' ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
    </button>
  );

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-6">
          <h1 className="text-3xl font-heading font-bold text-text-primary" data-testid="portfolio-heading">Portfolio</h1>
          <button
            onClick={downloadPortfolioUpdates}
            disabled={downloading || filtered.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold text-text-primary border border-border rounded-lg hover:border-primary/50 hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="download-portfolio-updates"
            title="Download all portfolio monthly updates"
          >
            <DownloadSimple size={15} weight="bold" />
            {downloading ? 'Preparing...' : 'Download Updates'}
          </button>
        </div>

        {/* Aggregates */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-surface border border-border rounded-[10px] p-5">
            <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Companies</div>
            <div className="text-2xl font-mono font-semibold text-text-primary">{agg.total_companies || 0}</div>
          </div>
          <div className="bg-surface border border-border rounded-[10px] p-5">
            <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Fund MoIC</div>
            <div className={`text-2xl font-mono font-semibold ${agg.fund_moic ? (agg.fund_moic >= 1 ? 'text-success' : 'text-danger') : 'text-text-muted'}`}>{agg.fund_moic ? `${agg.fund_moic}x` : 'Pending'}</div>
          </div>
          <div className="bg-surface border border-border rounded-[10px] p-5">
            <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Total MRR</div>
            <div className="text-2xl font-mono font-semibold text-text-primary">{formatCompact(agg.total_mrr)}</div>
          </div>
          <div className="bg-surface border border-border rounded-[10px] p-5">
            <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Total Cash</div>
            <div className="text-2xl font-mono font-semibold text-text-primary">{formatCompact(agg.total_cash)}</div>
          </div>
          <div className="bg-surface border border-border rounded-[10px] p-5">
            <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Total Revenue</div>
            <div className="text-2xl font-mono font-semibold text-text-primary">{formatCompact(agg.total_revenue)}</div>
          </div>
          <div className="bg-surface border border-border rounded-[10px] p-5">
            <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Avg Margin</div>
            <div className="text-2xl font-mono font-semibold text-text-primary">{agg.avg_gross_margin != null ? `${agg.avg_gross_margin}%` : '--'}</div>
          </div>
          <div className="bg-surface border border-border rounded-[10px] p-5">
            <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Headcount</div>
            <div className="text-2xl font-mono font-semibold text-text-primary">{agg.total_headcount || 0}</div>
          </div>
          <div className="bg-surface border border-border rounded-[10px] p-5">
            <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Needs Attention</div>
            <div className={`text-2xl font-mono font-semibold ${agg.needs_attention > 0 ? 'text-danger' : 'text-success'}`}>{agg.needs_attention || 0}</div>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="search-filter-bar">
          <div className="flex items-center gap-2 border border-border rounded-lg px-3 py-2 flex-1 min-w-[200px] max-w-[350px]">
            <MagnifyingGlass size={16} className="text-text-muted" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies..." className="bg-transparent text-sm text-text-primary placeholder-text-muted focus:outline-none flex-1" data-testid="portfolio-search" />
            {search && <button onClick={() => setSearch('')} className="text-text-muted hover:text-text-primary"><X size={14} /></button>}
          </div>
          <select value={filterSector} onChange={e => setFilterSector(e.target.value)} className="bg-transparent border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none" data-testid="filter-sector">
            <option value="All">All Sectors</option>
              {SECTORS.map((sector) => (
                <option key={sector} value={sector}>
                  {sector}
                </option>))}
          </select>
          <select value={filterStage} onChange={e => setFilterStage(e.target.value)} className="bg-transparent border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none" data-testid="filter-stage">
            {STAGES.map(s => <option key={s} value={s}>{s === 'All' ? 'All Stages' : s}</option>)}
          </select>
          {(filterSector !== 'All' || filterStage !== 'All' || search) && (
            <button onClick={() => { setSearch(''); setFilterSector('All'); setFilterStage('All'); }} className="text-xs text-primary hover:text-primary/80 transition-colors">Clear filters</button>
          )}
          <div className="ml-auto text-xs text-text-muted">{filtered.length} of {companies.length} companies</div>
        </div>

        {/* Table */}
        <div className="bg-surface border border-border rounded-[10px] overflow-hidden" data-testid="portfolio-table">
          {sorted.length === 0 ? (
            <div className="p-12 text-center">
              <ChartLine size={48} className="text-text-muted mx-auto mb-4" weight="thin" />
              <p className="text-text-secondary mb-4">{companies.length === 0 ? 'No portfolio companies yet' : 'No companies match your filters'}</p>
            </div>
          ) : (
            <>
              <div className="hidden md:grid grid-cols-[2.5fr,1fr,0.8fr,1fr,0.8fr,0.7fr,0.7fr,0.7fr,32px] gap-3 px-5 py-3 border-b border-border bg-background/50">
                <div className="flex items-center gap-3">
                  <SortHeader label="Company" sortKeyName="startup_name" />
                  <span className="hidden lg:block text-[11px] uppercase tracking-wider text-text-muted ml-auto pr-1">Fund</span>
                </div>
                <SortHeader label="MRR" sortKeyName="mrr" />
                <SortHeader label="Growth" sortKeyName="growth" />
                <SortHeader label="Cash" sortKeyName="cash" />
                <SortHeader label="Runway" sortKeyName="runway" />
                <SortHeader label="Margin" sortKeyName="margin" />
                <SortHeader label="Churn" sortKeyName="churn" />
                <SortHeader label="MoIC" sortKeyName="moic" />
                <div />
              </div>
              <div className="divide-y divide-border">
                {sorted.map(c => {
                  const isExpanded = expandedId === c.id;
                  const lu = c.latest_update;
                  const rm = lu?.runway_months;
                  return (
                    <div key={c.id} data-testid={`company-row-${c.id}`}>
                      <div className={`grid grid-cols-1 md:grid-cols-[2.5fr,1fr,0.8fr,1fr,0.8fr,0.7fr,0.7fr,0.7fr,32px] gap-3 px-5 py-3 cursor-pointer hover:bg-background/30 transition-colors items-center ${isExpanded ? 'bg-background/40' : ''}`} onClick={() => setExpandedId(isExpanded ? null : c.id)}>
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getHealthDot(c)}`} />
                          {c.logo_url ? <img src={fileUrl(c.logo_url)} className="w-8 h-8 rounded object-cover flex-shrink-0" alt="" /> : <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center flex-shrink-0"><span className="text-primary font-mono text-xs font-bold">{c.startup_name?.substring(0, 2).toUpperCase()}</span></div>}
                          <div className="min-w-0">
                            <div className="font-medium text-text-primary text-sm truncate flex items-center gap-1.5">
                              {c.startup_name}
                              {(c.stale?.level === 'stale' || c.stale?.level === 'never') && (
                                <span
                                  data-testid={`stale-badge-${c.id}`}
                                  title={c.stale.level === 'never' ? 'This founder has never submitted a monthly update' : `Last update was ${c.stale.months_since} months ago`}
                                  className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${c.stale.level === 'never' ? 'bg-text-muted/15 text-text-muted' : 'bg-warning/15 text-warning'}`}
                                >
                                  {c.stale.label}
                                </span>
                              )}
                              {c.stale?.level === 'aging' && (
                                <span data-testid={`stale-badge-${c.id}`} title="Last update was 2 months ago" className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-info/15 text-info">
                                  {c.stale.label}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-text-muted truncate">{c.sector} · {c.funding_stage}</div>
                          </div>
                          <div className="hidden lg:block w-[110px] flex-shrink-0">
                            {c.my_investment?.primary_fund_name ? (
                              <div className="text-[11px] font-mono text-text-secondary truncate" title={c.my_investment.fund_names.join(', ')} data-testid={`fund-tag-${c.id}`}>
                                {c.my_investment.primary_fund_name}
                                {c.my_investment.fund_names.length > 1 && <span className="text-text-muted"> +{c.my_investment.fund_names.length - 1}</span>}
                              </div>
                            ) : (
                              <span className="text-[11px] text-text-muted italic">Unassigned</span>
                            )}
                          </div>
                        </div>
                        <div className="hidden md:block"><div className="font-mono text-sm font-semibold text-text-primary">{lu ? formatCompact(lu.mrr) : '--'}</div></div>
                        <div className="hidden md:block"><MomBadge value={c.mom?.mrr_pct} small /></div>
                        <div className="hidden md:block"><div className="font-mono text-sm text-text-primary">{lu ? formatCompact(lu.cash_in_bank) : '--'}</div></div>
                        <div className="hidden md:block"><span className={`font-mono text-sm font-semibold ${!rm ? 'text-text-muted' : rm < 3 ? 'text-danger' : rm < 6 ? 'text-warning' : rm < 12 ? 'text-info' : 'text-success'}`}>{rm ? `${rm.toFixed(1)} mo` : '--'}</span></div>
                        <div className="hidden md:block"><span className="font-mono text-sm text-text-primary">{lu?.gross_margin_pct != null ? `${lu.gross_margin_pct}%` : '--'}</span></div>
                        <div className="hidden md:block"><span className={`font-mono text-sm ${lu?.churn_pct > 10 ? 'text-danger' : lu?.churn_pct > 5 ? 'text-warning' : 'text-text-primary'}`}>{lu?.churn_pct != null ? `${parseFloat(lu.churn_pct.toFixed(1))}%` : '--'}</span></div>
                        <div className="hidden md:block"><span className={`font-mono text-sm font-semibold ${c.moic?.moic ? (c.moic.moic >= 3 ? 'text-success' : c.moic.moic >= 1 ? 'text-info' : 'text-danger') : 'text-text-muted'}`} data-testid={`moic-${c.id}`}>{c.moic?.moic ? `${c.moic.moic}x` : 'Pending'}</span></div>
                        <div className="flex justify-end">{isExpanded ? <CaretUp size={16} className="text-text-muted" /> : <CaretDown size={16} className="text-text-muted" />}</div>
                      </div>
                      {isExpanded && <ExpandedDetail company={c} onDelete={(id, name) => setDeleteTarget({ id, name })} onRoundEdit={(r) => setEditingRound(r)} />}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      {deleteTarget && <DeleteModal startupName={deleteTarget.name} onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />}

      {/* Edit Round Modal */}
      {editingRound && (
        <EditRoundModal
          round={editingRound}
          onClose={() => setEditingRound(null)}
          onSaved={() => { setEditingRound(null); if (onRefresh) onRefresh(); }}
        />
      )}
    </div>
  );
};

export default VCPortfolio;
